import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { api } from '@services/api/api';
import { persistStore, persistReducer } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
import AppBarStateObservable from '@components/AppBar/AppBarStateObservable';
import { AppBarLoader } from '@components/AppBar/app-bar-loader';
import { LanguageLocale, LanguageName, LoginState, Theme } from '@components/AppBar/app-bar-state';

import userReducer, { setTheme, setLanguage } from './reducers/user.reducer';
import interviewReducer, { resetAllDialogsState } from './reducers/interview.reducer';
import projectReducer, { resetProjectModalsState } from './reducers/project.reducer';
import projectAnalyseReducer, { resetAnalysisDialogsState } from './reducers/projectAnalysis.reducer';
import translationsReducer from './reducers/translation.reducer';

const persistConfig = {
  key: 'root',
  storage: storageSession,
  blacklist: [api.reducerPath],
};

const rootReducer = combineReducers({
  user: userReducer,
  interview: interviewReducer,
  projects: projectReducer,
  projectAnalyse: projectAnalyseReducer,
  translations: translationsReducer,
  [api.reducerPath]: api.reducer,
});

export const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }).concat(api.middleware),
});

export const persistor = persistStore(store);

persistor.subscribe(() => {
  store.dispatch(resetAllDialogsState());
  store.dispatch(resetProjectModalsState());
  store.dispatch(resetAnalysisDialogsState());
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const appBarStateObservable = new AppBarStateObservable();
const appBarLoader = new AppBarLoader();

appBarStateObservable.attach((appBarState) => {
  if (appBarState.loggedInState === LoginState.LoggingIn) {
    sessionStorage.removeItem('persist:root');
  }

  const lang = store.getState().user.language;
  const savedLang = localStorage.getItem('lang');

  if (!lang && !savedLang) {
    appBarLoader.setLanguage(LanguageLocale.English);
  } else if (!lang && savedLang) {
    const lang = savedLang === 'DE' ? LanguageLocale.German : LanguageLocale.English;
    appBarLoader.setLanguage(lang);
    store.dispatch(setLanguage(savedLang as LanguageName));
  } else if (lang !== appBarState.language.name) {
    store.dispatch(setLanguage(appBarState.language.name));
    localStorage.setItem('lang', appBarState.language.name);
  }

  const currentTheme = store.getState().user.theme;
  const savedTheme = localStorage.getItem('theme');

  if (savedTheme) {
    appBarLoader.setTheme(savedTheme as Theme);
    store.dispatch(setTheme(savedTheme as Theme));
  } else {
    appBarLoader.setTheme(Theme.Light);
  }

  if (currentTheme !== appBarState.theme) {
    localStorage.setItem('theme', appBarState.theme);
  }
});
